import { Scalar, Vector2, Vector3 } from '@babylonjs/core';
import {
  CommonTouchBase,
  createEasingFunction,
  EasingFunctionType,
  EasingModeType,
  InputCanvasProps,
  playAnimation,
  RotationOption,
  stopPlayingAnimation,
  TouchData,
  ZEDCamera,
} from '@modules/standard';

export interface WindowCameraDollyOption {
  originPosition: Vector3;
  originForward: Vector3;
  dollyPosition: Vector2;
  initialAlpha: number;
  option: {
    sensibilityDolly: number;
    sensibilityPan: number;
    dollyRatio: number;
    dollyDistance: number;
    alphaMin: number;
    alphaMax: number;
    betaMin: number;
    betaMax: number;
  };
}

export const WindowCameraDolly: CommonTouchBase & RotationOption & WindowCameraDollyOption = {
  type: 'CommonTouch',
  touchCount: 1,
  key: 'WindowCameraDolly',
  name: 'WindowCameraDolly',
  option: {
    mouseButton: 'all',
    mouseKeyCode: 'none',
    alpha: true,
    beta: true,
    inertia: 0,
    sensibilityX: 0.002,
    sensibilityY: 0.002,
    sensibilityDolly: 0.001,
    sensibilityPan: 0.002,
    dollyRatio: 0.05,
    dollyDistance: 0.5,
    alphaMin: Math.PI * 0.25,
    alphaMax: -Math.PI * 0.25,
    betaMin: Math.PI * 0.75 + Math.PI * 0.5,
    betaMax: -Math.PI * 0.75 + Math.PI * 0.5,
  },
  initialAlpha: 0,
  originPosition: Vector3.ZeroReadOnly,
  originForward: Vector3.ZeroReadOnly,
  dollyPosition: Vector2.Zero(),
  _lastDollyPosition: Vector2.Zero(),
  attachControl(_camera: ZEDCamera, _canvasProps: InputCanvasProps, _isMobileOrTablet: boolean, _noPreventDefault?: boolean | undefined) {
    this.dollyPosition.setAll(0);
    this.originPosition = _camera.position.clone();

    this.originForward = _camera.target.subtract(_camera.position).normalize();
    this.initialAlpha = _camera.alpha;
  },
  detachControl(_camera: ZEDCamera, _isMobileOrTablet: boolean) {},
  checkInputs(camera: ZEDCamera) {
    if (camera.getScene().getAllAnimatablesByTarget(camera).length > 0) return;
    if (this['_lastDollyPosition'].equals(this.dollyPosition)) return;

    const dollyLength = this.dollyPosition.length();
    const dollyTrack = Scalar.InverseLerp(0, this.option.dollyRatio, dollyLength);

    camera.setPosition(
      this.originPosition.add(this.originForward.scale(dollyTrack * this.option.dollyDistance)),
      false,
      false
    );

    const dollyRemains = Scalar.InverseLerp(this.option.dollyRatio, 1, dollyLength);

    const alphaOffset = dollyLength < this.option.dollyRatio ? 0 : (this.dollyPosition.x / dollyLength) * dollyRemains;

    camera.alpha =
      this.initialAlpha +
      Scalar.Lerp(this.option.alphaMin, this.option.alphaMax, Scalar.InverseLerp(-1, 1, alphaOffset));

    const betaOffset = dollyLength < this.option.dollyRatio ? 0 : (this.dollyPosition.y / dollyLength) * dollyRemains;

    camera.beta = Scalar.Lerp(this.option.betaMin, this.option.betaMax, Scalar.InverseLerp(-1, 1, betaOffset));

    this['_lastDollyPosition'].copyFrom(this.dollyPosition);
  },
  reset() {},
  onMousedown(camera, _touchData, _canvasProps) {
    camera.stopPlayingAnimation();
    stopPlayingAnimation(camera.getScene(), this);
  },
  onMousemove(camera: ZEDCamera, touchData: TouchData, _canvasProps: InputCanvasProps) {
    const movement = touchData.movement;
    const isDolly = this.dollyPosition.length() <= this.option.dollyRatio;
    const sensibility = isDolly ? this.option.sensibilityDolly : this.option.sensibilityPan;
    Vector2.ClampToRef(
      this.dollyPosition.add(movement.scale(sensibility)),
      new Vector2(-1, -1),
      new Vector2(1, 1),
      this.dollyPosition
    );
  },
  onMouseup(_camera, _touchData, _canvasProps) {
    if (this.dollyPosition.length() <= this.option.dollyRatio) {
      playAnimation(
        {
          target: this,
          property: [
            {
              targetProperty: 'dollyPosition',
              keyFrame: {
                duration: 0.2,
                from: this.dollyPosition,
                to: Vector2.ZeroReadOnly,
              },
            },
          ],
          easingFunction: createEasingFunction(EasingFunctionType.QuarticEase, EasingModeType.InOut),
        },
        undefined,
        true
      );
    }
  },
  onTouchStart(camera, _touchData, _canvasProps) {
    if (!_touchData[0]) return;
    this.onMousedown(camera, _touchData[0], _canvasProps);
  },
  onTouchMoved(camera, touchData, _canvasProps) {
    if (!touchData[0]) return;
    this.onMousemove(camera, touchData[0], _canvasProps);
  },
  onTouchEnd(camera, touchData, _canvasProps) {
    if (!touchData[0]) return;
    this.onMouseup(camera, touchData[0], _canvasProps);
  },
};
